import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link, NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { REACT_APP_API_URL } from "../../env";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { fetchUserDetails } from "../../store/slice/features/authSlice";
import { useDispatch } from "react-redux";

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaText, setCaptchaText] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const loadCaptcha = async () => {
    try {
      const res = await axios.get(
        `${REACT_APP_API_URL}/api/hrms/auth/captcha`
      );
      setCaptchaText(res.data.captcha);
      setCaptchaId(res.data.captchaId);
      setCaptchaValue("");
    } catch {
      toast.error("Unable to load captcha");
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const isValidIdentifier = (input) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;
    const userIdRegex = /^[a-zA-Z0-9_]{3,}$/;
    return emailRegex.test(input) || phoneRegex.test(input) || userIdRegex.test(input);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!isValidIdentifier(identifier)) {
      toast.error("Please enter a valid email, phone number, or user ID.");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    if (!captchaId || !captchaValue.trim()) {
      toast.error("Please enter captcha");
      return;
    }

    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}/api/auth/login`,
        {
          identifier,
          password,
          captchaId,
          captchaValue: captchaValue.trim(),
        },
        { withCredentials: true }
      );

      dispatch(fetchUserDetails());

      if (response.status === 200) {
        toast.success("Login successful");
        const from = location.state?.from || "/";
        navigate(from);
      }
    }
    catch (error) {
      console.error("Login error:", error);
      toast.error(
        error.response?.data?.message ||
        "Login failed. Please check your credentials."
      );
      loadCaptcha();
    }
  };

  return (
    <div className="login-background-image d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
      <div className="container" style={{ maxWidth: "400px" }}>
        <form
          className="p-4 rounded text-center"
          style={{ backgroundColor: "rgba(63, 61, 61, 0.7)" }}
          onSubmit={handleSignIn}
        >
          <div className="d-flex justify-content-center mb-2">
            <NavLink to="/">
              <img
                src="images/logo/JTDC.png"
                style={{ width: "200px", cursor: "pointer" }}
                alt="Logo"
                loading="lazy"
              />
            </NavLink>
          </div>

          <h5 className="text-center text-white auth-page-title">Login</h5>

          <div className="form-group-signup">

            <input
              type="text"
              className="form-control"
              id="identifier"
              placeholder="Enter Email or Phone"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            {!isValidIdentifier(identifier) && identifier.length > 0 && (
              <small className="text-danger">
                Enter a valid email, phone number, or user ID.
              </small>
            )}
          </div>

          <div className="form-group-signup position-relative mt-4">
            <div className="position-relative">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control pe-5"
                id="password"
                placeholder="Enter Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span
                className="position-absolute d-flex align-items-center"
                style={{
                  top: "50%",
                  right: "15px",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>

          <div className="form-group-signup">
            {/* ================= DESKTOP / LAPTOP VIEW  ================= */}
            <div className="input-group d-none d-md-flex">
              <input
                type="text"
                className="form-control text-center fw-semibold"
                value={captchaText}
                readOnly
                style={{
                  letterSpacing: "3px",
                  fontFamily: "monospace",
                  userSelect: "none",
                  backgroundColor: "#f8f9fa",
                  flex: 5,
                }}
              />
              <button
                type="button"
                className="btn p-0 form-control"
                onClick={loadCaptcha}
                title="Refresh Captcha"
                style={{
                  flex: 1,
                  minWidth: "38px",
                  color: "black",
                  fontSize: "1.4rem"
                }}
              >
                ↻
              </button>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Captcha"
                value={captchaValue}
                onChange={(e) =>
                  setCaptchaValue(e.target.value)}
                autoComplete="off"
                required
                style={{
                  flex: 7,
                }}
              />
            </div>

            {/* ================= MOBILE VIEW ================= */}
            <div className="d-flex d-md-none flex-column">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control text-center fw-semibold"
                  value={captchaText}
                  readOnly
                  style={{
                    letterSpacing: "6px",
                    fontFamily: "monospace",
                    userSelect: "none",
                    backgroundColor: "#f8f9fa",
                    flex: 3,
                    fontSize: "1.2rem"
                  }}
                />
                <button
                  type="button"
                  className="btn form-control p-0 px-2"
                  onClick={loadCaptcha}
                  title="Refresh Captcha"
                  style={{ flex: 1, color: "black", fontSize: "1.5rem" }}
                >
                  ↻
                </button>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Captcha"
                value={captchaValue}
                onChange={(e) =>
                  setCaptchaValue(e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/forgot-password"
              style={{ color: "#ffeb3b", fontStyle: "italic" }}
            >
              Forgot Password?
            </Link>
          </div>

          <div className="d-flex justify-content-center mt-10">
            <button
              type="submit"
              className="btn btn-block"
              style={{ backgroundColor: "#4b9f16", color: "white" }}
            >
              Login
            </button>
          </div>

          <p className="text-center mt-2 text-white">
            Don't have an account?{" "}
            <Link
              to="/signup"
              style={{
                color: "#ffeb3b",
                textDecoration: "underline",
                fontStyle: "italic",
              }}
            >
              Signup
            </Link>
          </p>

          <p className="text-center mt-2 text-white">
            Login as{" "}
            <Link
              to="https://jtdclportal.jharkhand.gov.in/signin"
              style={{
                color: "#ffeb3b",
                textDecoration: "underline",
                fontStyle: "italic",
              }}
            >
              Admin/Manager
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;